import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../features/userSlice";
import { BizDevelopmentDashboardMobileEndPointLogic } from "../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardMobileEndPointLogic";
import { BizDevelopmentDashboardEndPointLogic } from "../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardEndPointLogic";
import {
  setMobileComparisonQueryOutput,
  setMobileComparisonFetchedFor,
} from "../../../../features/bizDevelopmentDashboardMobileSlice";
import {
  setLoaderMissingData,
  setLoaderFtdData,
  setLoaderAnomaliesBrand,
  setLoaderIncomeData,
  setLoaderEarningData,
} from "../../../../features/bizDevelopmentAnomaliesSlice";
import store from "../../../../store";
import {
  BIZ_DEVELOPMENT_BUDGET_QUERY_ID,
  BIZ_DEVELOPMENT_BUDGET_LAST_RUN_QUERY_ID,
} from "../QueryIDsBizDevelopmentDashboard";
import dayjs from "dayjs";

export const useBizDevelopmentDashboardMobile = () => {
  const [searchParams] = useSearchParams();
  const userData = useSelector(selectUserData);
  const oauthTokenHeader = userData?.data?.oauth_token;

  const getSiteFromWorkspace = (): string[] => {
    const workspaceId = searchParams.get("workspace");
    return workspaceId ? workspaceId.split(",").filter(Boolean) : [];
  };

  const getTimeframeFromParams = (): string => {
    return searchParams.get("timeframePerformance") || "yesterday";
  };

  const fetchDashboardData = async (
    timeframe?: string,
    siteSharedUids?: string[],
    customRange?: { startDate: string; endDate: string },
  ) => {
    const resolvedTimeframe = timeframe ?? getTimeframeFromParams();
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;

    await BizDevelopmentDashboardMobileEndPointLogic.getDashboardData(
      oauthTokenHeader,
      resolvedSites,
      resolvedTimeframe,
      customRange,
    );
  };

  const fetchComparisonData = async (
    timeframe: string,
    siteSharedUids?: string[],
    customRange?: { startDate: string; endDate: string },
  ) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;

    await BizDevelopmentDashboardMobileEndPointLogic.getComparisonDashboardData(
      oauthTokenHeader,
      resolvedSites,
      timeframe,
      customRange,
    );
  };

  const clearComparisonData = () => {
    store.dispatch(setMobileComparisonQueryOutput([]));
    store.dispatch(setMobileComparisonFetchedFor(null));
  };

  const fetchBudgetData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    // Fire budget data and brand last-run queries simultaneously
    await Promise.all([
      BizDevelopmentDashboardMobileEndPointLogic.getBizMobileBudgetData({
        oauthTokenHeader,
        queryId: BIZ_DEVELOPMENT_BUDGET_QUERY_ID,
        websiteSharedUids: resolvedSites,
        type: "site",
        dimension: ["bbb_uk"],
      }),
      BizDevelopmentDashboardMobileEndPointLogic.getBudgetCardBrandLastRunQueryInBizDevDashboardMobile(
        {
          oauthTokenHeader,
          queryId: BIZ_DEVELOPMENT_BUDGET_LAST_RUN_QUERY_ID,
          websiteSharedUids: resolvedSites,
          type: "site",
          dimension: ["bbb_uk"],
        },
      ),
    ]);
  };

  const fetchBeAwareFraudPlayersData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardMobileEndPointLogic.getBeAwareFraudPlayersData(
      oauthTokenHeader,
      resolvedSites,
    );
  };

  const fetchBeAwareNegativeBalanceData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardMobileEndPointLogic.getBeAwareNegativeBalanceData(
      oauthTokenHeader,
      resolvedSites,
    );
  };

  const fetchBeAwareConversionsUploadData = async (
    siteSharedUids?: string[],
  ) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardMobileEndPointLogic.getBeAwareConversionsUploadData(
      oauthTokenHeader,
      resolvedSites,
    );
  };

  // Anomalies - Missing Data Tab. Dispatches into the shared
  // bizDevelopmentAnomaliesSlice via the desktop logic (device-agnostic).
  // The desktop logic only toggles the generic dashboard-wide loading flag,
  // so this owns loaderMissingData itself for the mobile skeleton.
  const fetchMissingData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    store.dispatch(setLoaderMissingData(true));
    try {
      await BizDevelopmentDashboardEndPointLogic.getAnomaliesMissingData({
        oauthTokenHeader,
        websiteSharedUids: resolvedSites,
        type: "site",
        dimension: ["bbb_uk"],
      });
    } finally {
      store.dispatch(setLoaderMissingData(false));
    }
  };

  // Anomalies - FTD Tab > 5+ Clicks with 0 FTD (Last 7 Days). Independent of
  // Problematic FTDs below — each sub-tab fetches only its own query, only
  // when it's actually opened (never both at once).
  // The desktop logic doesn't toggle any loading flag for this query, so this
  // owns loaderFtdData itself for the mobile skeleton.
  const fetchClicksWithFtdData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    store.dispatch(setLoaderFtdData(true));
    try {
      await BizDevelopmentDashboardEndPointLogic.getAnomaliesPerformanceFtdData({
        oauthTokenHeader,
        websiteSharedUids: resolvedSites,
        type: "site",
        dimension: ["bbb_uk"],
      });
    } finally {
      store.dispatch(setLoaderFtdData(false));
    }
  };

  // Anomalies - FTD Tab > Problematic FTDs (Last 60 Days). Independent of
  // 5+ Clicks with 0 FTD above — see note there.
  const fetchProblematicFtdData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    store.dispatch(setLoaderFtdData(true));
    try {
      await BizDevelopmentDashboardEndPointLogic.getAnomaliesProblematicFtdData({
        oauthTokenHeader,
        websiteSharedUids: resolvedSites,
        type: "site",
        dimension: ["bbb_uk"],
      });
    } finally {
      store.dispatch(setLoaderFtdData(false));
    }
  };

  // Anomalies - Brand Tab > Without FTD & NetRev (Last 60 Days). Independent
  // of No Player Report below — see FTD's ClicksWithFtd/ProblematicFtds note.
  // No dedicated slice loader exists for this tab besides loaderAnomaliesBrand,
  // shared by both Brand sub-tabs (mirrors loaderFtdData's shared use).
  const fetchWithoutFtdAndNetRevData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    store.dispatch(setLoaderAnomaliesBrand(true));
    try {
      await BizDevelopmentDashboardEndPointLogic.getWithoutFtdAndNetRevBrandData(
        {
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type: "site",
          dimension: ["bbb_uk"],
        },
      );
    } finally {
      store.dispatch(setLoaderAnomaliesBrand(false));
    }
  };

  // Anomalies - Brand Tab > No Player Report (Last 60 Days). Independent of
  // Without FTD & NetRev above. This is the one Brand query that takes a real
  // date range — same today−59-to-today window the desktop hook computes.
  const fetchGapsBetweenPlayersData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    const startDate = dayjs().subtract(59, "day").format("YYYY-MM-DD");
    const endDate = dayjs().format("YYYY-MM-DD");
    store.dispatch(setLoaderAnomaliesBrand(true));
    try {
      await BizDevelopmentDashboardEndPointLogic.getGapsBetweenPlayersAndDailyPerformance(
        {
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type: "site",
          dimension: ["bbb_uk"],
          startDate,
          endDate,
        },
      );
    } finally {
      store.dispatch(setLoaderAnomaliesBrand(false));
    }
  };

  // Anomalies - Income Tab > Income Gaps in MTD. Independent of Decrease in
  // Daily Income below — see FTD's ClicksWithFtd/ProblematicFtds note.
  const fetchIncomeGapsData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    store.dispatch(setLoaderIncomeData(true));
    try {
      await BizDevelopmentDashboardEndPointLogic.getIncomeGapsInMTDData({
        oauthTokenHeader,
        websiteSharedUids: resolvedSites,
        type: "site",
        dimension: ["bbb_uk"],
      });
    } finally {
      store.dispatch(setLoaderIncomeData(false));
    }
  };

  // Anomalies - Income Tab > Decrease in Daily Income in MTD. Independent of
  // Income Gaps above.
  const fetchDecreaseDailyIncomeData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    store.dispatch(setLoaderIncomeData(true));
    try {
      await BizDevelopmentDashboardEndPointLogic.getDecreaseDailyIncomeGapsMTDData(
        {
          oauthTokenHeader,
          websiteSharedUids: resolvedSites,
          type: "site",
          dimension: ["bbb_uk"],
        },
      );
    } finally {
      store.dispatch(setLoaderIncomeData(false));
    }
  };

  // Anomalies - Earning Tab > Daily Earning Gaps in MTD. Independent of
  // Monthly Earning Gaps below — see FTD's ClicksWithFtd/ProblematicFtds note.
  const fetchDailyEarningGapsData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    store.dispatch(setLoaderEarningData(true));
    try {
      await BizDevelopmentDashboardEndPointLogic.getDailyEarningGapsMTDData({
        oauthTokenHeader,
        websiteSharedUids: resolvedSites,
        type: "site",
        dimension: ["bbb_uk"],
      });
    } finally {
      store.dispatch(setLoaderEarningData(false));
    }
  };

  // Anomalies - Earning Tab > Monthly Earning Gaps in MTD. Independent of
  // Daily Earning Gaps above.
  const fetchMonthlyEarningGapsData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSiteFromWorkspace();
    if (resolvedSites.length === 0) return;
    store.dispatch(setLoaderEarningData(true));
    try {
      await BizDevelopmentDashboardEndPointLogic.getMonthlyEarningGapsMTDData({
        oauthTokenHeader,
        websiteSharedUids: resolvedSites,
        type: "site",
        dimension: ["bbb_uk"],
      });
    } finally {
      store.dispatch(setLoaderEarningData(false));
    }
  };

  return {
    fetchDashboardData,
    fetchComparisonData,
    clearComparisonData,
    fetchBudgetData,
    getSiteFromWorkspace,
    getTimeframeFromParams,
    fetchBeAwareFraudPlayersData,
    fetchBeAwareNegativeBalanceData,
    fetchBeAwareConversionsUploadData,
    fetchMissingData,
    fetchClicksWithFtdData,
    fetchProblematicFtdData,
    fetchWithoutFtdAndNetRevData,
    fetchGapsBetweenPlayersData,
    fetchIncomeGapsData,
    fetchDecreaseDailyIncomeData,
    fetchDailyEarningGapsData,
    fetchMonthlyEarningGapsData,
  };
};
